/* =========================================================
   silklife theme scripts
   Cart uses Shopify's AJAX API (/cart/add.js, /cart/change.js);
   routes, currency and strings come from window.silklife in
   layout/theme.liquid.
   ========================================================= */

const config = window.silklife;
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const money = (cents) => new Intl.NumberFormat(config.locale, { style: "currency", currency: config.currency }).format(cents / 100);
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const icon = (id) => `<svg aria-hidden="true"><use href="#${id}"/></svg>`;

/* ---------- Drawers ---------- */
let lastFocus = null;

function openDrawer(id) {
  const drawer = document.getElementById(id);
  if (!drawer) return;
  lastFocus = document.activeElement;
  drawer.classList.add("is-open");
  drawer.setAttribute("aria-hidden", "false");
  document.body.classList.add("is-locked");
  const focusable = $("button, a", $(".drawer__panel", drawer));
  if (focusable) focusable.focus();
}

function closeDrawers() {
  $$("[data-drawer].is-open").forEach((d) => {
    d.classList.remove("is-open");
    d.setAttribute("aria-hidden", "true");
  });
  document.body.classList.remove("is-locked");
  if (lastFocus) lastFocus.focus();
}

document.addEventListener("click", (e) => {
  const opener = e.target.closest("[data-open]");
  if (opener) { openDrawer(opener.dataset.open); return; }
  if (e.target.closest("[data-drawer] [data-close]") || e.target.closest("[data-close-links] a")) closeDrawers();
});
document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeDrawers(); });

/* ---------- Toast ---------- */
const toast = document.createElement("div");
toast.className = "toast";
toast.setAttribute("role", "status");
document.body.appendChild(toast);
let toastTimer;
function showToast(msg) {
  toast.textContent = msg;
  toast.classList.add("is-visible");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 2400);
}

/* ---------- Cart ---------- */
const cartEl = $(".cart");

async function cartRequest(url, body) {
  const res = await fetch(url, {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.description || data.message || config.strings.error);
  return data;
}

const fetchCart = () => cartRequest(`${config.routes.cart}.js`);

function renderCart(cart) {
  const count = cart.item_count;
  $$("[data-cart-count]").forEach((el) => { el.textContent = count; el.hidden = count === 0; });
  $("[data-cart-count-label]").textContent = count ? `(${count})` : "";
  $("[data-cart-subtotal]").textContent = money(cart.total_price);
  cartEl.classList.toggle("is-empty", count === 0);

  const msg = $("[data-shipping-msg]");
  if (msg && config.freeShippingAt > 0) {
    const left = config.freeShippingAt - cart.total_price;
    msg.textContent = left > 0 ? config.strings.shippingLeft.replace("[amount]", money(left)) : config.strings.shippingDone;
    $("[data-shipping-bar]").style.width = Math.min(100, (cart.total_price / config.freeShippingAt) * 100) + "%";
  }

  $("[data-cart-items]").innerHTML = cart.items.map((item) => {
    const options = (item.options_with_values || []).filter((o) => o.name !== "Title").map((o) => o.value).join(" · ");
    const image = item.image ? `${item.image}${item.image.includes("?") ? "&" : "?"}width=200` : "";
    return `
      <div class="cart-item" data-key="${escapeHtml(item.key)}">
        ${image ? `<img src="${escapeHtml(image)}" alt="">` : "<span></span>"}
        <div>
          <a class="cart-item__name" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${options ? `<p class="cart-item__variant">${escapeHtml(options)}</p>` : ""}
          <p class="cart-item__price">${money(item.final_price)}</p>
          <div class="qty" aria-label="Quantity">
            <button type="button" aria-label="${escapeHtml(config.strings.decrease)}" data-qty="${item.quantity - 1}">${icon("i-minus")}</button>
            <span>${item.quantity}</span>
            <button type="button" aria-label="${escapeHtml(config.strings.increase)}" data-qty="${item.quantity + 1}">${icon("i-plus")}</button>
          </div>
          <button class="cart-item__remove" type="button" data-qty="0">${escapeHtml(config.strings.remove)}</button>
        </div>
        <p class="cart-item__total">${money(item.final_line_price)}</p>
      </div>`;
  }).join("");
}

async function addToCart(id, quantity, button) {
  if (button) button.disabled = true;
  try {
    await cartRequest(config.routes.cartAdd, { items: [{ id: Number(id), quantity }] });
    renderCart(await fetchCart());
    openDrawer("cart");
  } catch (err) {
    showToast(err.message);
  } finally {
    if (button) button.disabled = false;
  }
}

$("[data-cart-items]").addEventListener("click", async (e) => {
  const btn = e.target.closest("[data-qty]");
  const item = e.target.closest(".cart-item");
  if (!btn || !item) return;
  try {
    renderCart(await cartRequest(config.routes.cartChange, { id: item.dataset.key, quantity: Number(btn.dataset.qty) }));
  } catch (err) {
    showToast(err.message);
  }
});

document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-add]");
  if (btn && !btn.disabled) addToCart(btn.dataset.add, Number(btn.dataset.qty || 1), btn);
});

const initialCart = $("[data-cart-json]");
if (initialCart) renderCart(JSON.parse(initialCart.textContent));

/* ---------- Product forms (switcher panels and product page) ---------- */
function initProductForm(form) {
  const addBtn = $("[data-add]", form);
  const priceEl = $("[data-price]", form);
  const qtyEl = $("[data-qty-value]", form);
  const options = $$("[data-option]", form);
  let qty = 1;
  // The form itself carries the selected variant; option buttons carry the alternatives.
  let current = options.find((o) => o.classList.contains("is-active")) || form;

  const update = () => {
    const price = Number(current.dataset.price);
    const compare = Number(current.dataset.compare);
    const available = current.dataset.available === "true";
    const save = compare > price ? Math.round((1 - price / compare) * 100) : 0;
    priceEl.innerHTML = `<strong>${money(price)}</strong>`
      + (save ? `<s>${money(compare)}</s><span class="price__save">-${save}%</span>` : "");
    qtyEl.textContent = qty;
    addBtn.dataset.add = current.dataset.variantId;
    addBtn.dataset.qty = qty;
    addBtn.disabled = !available;
    addBtn.textContent = available ? `${config.strings.addToCart} — ${money(price * qty)}` : config.strings.soldOut;
  };

  $$("[data-step]", form).forEach((b) => b.addEventListener("click", () => {
    qty = Math.max(1, qty + Number(b.dataset.step));
    update();
  }));
  options.forEach((o) => o.addEventListener("click", () => {
    current = o;
    options.forEach((b) => { b.classList.toggle("is-active", b === o); b.setAttribute("aria-checked", String(b === o)); });
    update();
  }));
}
$$("[data-product-form]").forEach(initProductForm);

/* Thumbnails swap the main image */
$$("[data-media]").forEach((media) => {
  const main = $(".switcher__main img", media);
  $$(".switcher__thumbs button", media).forEach((t) => t.addEventListener("click", () => {
    $$(".switcher__thumbs button", media).forEach((b) => b.classList.toggle("is-active", b === t));
    if (!main) return;
    main.removeAttribute("srcset");
    main.src = t.dataset.src;
  }));
});

/* ---------- Product switcher tabs ---------- */
$$("[data-switcher]").forEach((root) => {
  const tabs = $("[data-switcher-tabs]", root);
  const buttons = $$("[data-panel]", tabs);
  buttons.forEach((btn) => btn.addEventListener("click", () => {
    buttons.forEach((b) => {
      const on = b === btn;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-selected", String(on));
      document.getElementById(b.dataset.panel).hidden = !on;
    });
    tabs.scrollTo({ left: btn.offsetLeft - (tabs.clientWidth - btn.offsetWidth) / 2 });
  }));
});

/* ---------- Image galleries (spotlights) ---------- */
function initGallery(root) {
  const track = $("[data-track]", root);
  const slides = [...track.children];
  const dots = $("[data-dots]", root);
  dots.innerHTML = slides.map((_, i) => `<button type="button" aria-label="${i + 1}"></button>`).join("");
  const dotBtns = [...dots.children];

  const current = () => Math.round(track.scrollLeft / track.clientWidth);
  const go = (i) => track.scrollTo({ left: ((i + slides.length) % slides.length) * track.clientWidth });
  const sync = () => dotBtns.forEach((d, i) => d.classList.toggle("is-active", i === current()));

  $("[data-prev]", root).addEventListener("click", () => go(current() - 1));
  $("[data-next]", root).addEventListener("click", () => go(current() + 1));
  dotBtns.forEach((d, i) => d.addEventListener("click", () => go(i)));
  track.addEventListener("scroll", () => requestAnimationFrame(sync), { passive: true });
  sync();
}
$$("[data-gallery]").forEach(initGallery);

/* ---------- Horizontal scrollers ---------- */
function initScroller(root) {
  const track = $("[data-track]", root);
  const prev = $("[data-prev]", root);
  const next = $("[data-next]", root);
  const step = () => {
    const card = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return card ? card.getBoundingClientRect().width + gap : track.clientWidth;
  };
  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max;
  };
  prev.addEventListener("click", () => track.scrollBy({ left: -step() }));
  next.addEventListener("click", () => track.scrollBy({ left: step() }));
  track.addEventListener("scroll", () => requestAnimationFrame(update), { passive: true });
  window.addEventListener("resize", update);
  update();
}
$$("[data-scroller]").forEach(initScroller);

/* ---------- Before/after: avatars jump to a customer ---------- */
$$("[data-results]").forEach((scroller) => {
  const section = scroller.closest("section");
  const avatars = $$("[data-avatars] button", section);
  const track = $("[data-track]", scroller);
  const cards = [...track.children];
  const setActive = (i) => avatars.forEach((a, j) => {
    a.classList.toggle("is-active", i === j);
    a.setAttribute("aria-selected", String(i === j));
  });
  avatars.forEach((a, i) => a.addEventListener("click", () => {
    if (!cards[i]) return;
    track.scrollTo({ left: cards[i].offsetLeft - track.offsetLeft });
    setActive(i);
  }));
  track.addEventListener("scroll", () => requestAnimationFrame(() => {
    const x = track.scrollLeft;
    let best = 0;
    cards.forEach((c, i) => { if (Math.abs(c.offsetLeft - track.offsetLeft - x) < Math.abs(cards[best].offsetLeft - track.offsetLeft - x)) best = i; });
    setActive(best);
  }), { passive: true });
});

/* ---------- Generic tabs (FAQ) ---------- */
$$("[data-tabs]").forEach((tabs) => {
  const buttons = $$("[data-tab]", tabs);
  buttons.forEach((btn) => btn.addEventListener("click", () => {
    buttons.forEach((b) => {
      const on = b === btn;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-selected", String(on));
      const panel = document.getElementById(b.dataset.tab);
      panel.hidden = !on;
      panel.classList.toggle("is-active", on);
    });
  }));
});

/* ---------- Review filter ---------- */
$$("[data-review-tabs]").forEach((tabs) => {
  const buttons = $$("[data-filter]", tabs);
  const reviews = $$("[data-reviews] .review", tabs.closest("section"));
  buttons.forEach((btn) => btn.addEventListener("click", () => {
    buttons.forEach((b) => { b.classList.toggle("is-active", b === btn); b.setAttribute("aria-selected", String(b === btn)); });
    reviews.forEach((r) => { r.hidden = btn.dataset.filter !== "all" && r.dataset.product !== btn.dataset.filter; });
  }));
});

/* ---------- Header shadow + reveal on scroll ---------- */
const header = $("[data-header]");
if (header) {
  const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
}

if ("IntersectionObserver" in window && !window.Shopify?.designMode) {
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { en.target.classList.add("is-visible"); io.unobserve(en.target); }
  }), { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
  $$(".reveal").forEach((el) => io.observe(el));
} else {
  $$(".reveal").forEach((el) => el.classList.add("is-visible"));
}
